import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "semantic-ui-react";

/**
 * 指定席の抽選申込: 進行状況・請求と結果メールの枠（SeatOrderPage の上部）。
 *
 * 処理はすべて slash_admin（services/recital2027_seat_service.py）。
 * - 当落: 1次は申込一覧のチェックで決める（SeatOrderPage）。最初は全員当選。
 *   2次は申込一覧で部 × 席種ごとに当選席数を入れる（最初は優先の部だけ申込どおり）。
 *   1次・2次は SeatOrderPage の切り替えで分かれ、ここの処理もその次の申込だけが対象（data.round）。
 * - 請求: 支払期限を入れて「請求内容を確認」→「送信する」。送信を始めた時点で当落が確定する。
 *   5 件ずつ API を繰り返し呼び、進み具合を出す。途中で止めても、もう一度押せば続きから
 *   （二重には送られない）。失敗した家族は「要確認」に出て、1 件ずつ再送できる。
 *   自動引落の家族は登録のカードでその場で引き落とし、それ以外は請求書を送る（マイページアプリの設定）。
 * - 期限切れの未払い: まとめて当選を取り消せる（請求書を無効にし、取り消しのメールを送る）。
 *   落選者の繰り上げ当選は申込一覧の各行で選び、ここの「送信する」で請求書と繰り上げ当選のメールを送る。
 */

const STEPS = [
  { key: "deciding", label: "当落を決める" },
  { key: "sending", label: "請求・結果メール" },
  { key: "sent", label: "入金待ち" },
];

const COMPARE_LABELS = {
  same: "申込時の目安と一致",
  parts_changed: "出演部が変わった",
  mismatch: "金額が合わない",
  adjusted: "席数が申込と違う（比較しない）",
  unknown: "申込時の金額が無い（比較できない）",
};

const BATCH = 5;

const yen = (n) => `¥${(n || 0).toLocaleString()}`;

// 支払期限の初期値。slash_website の申込ページ（SeatPurchase.js の PAYMENT_DUE）に出している
// 「お支払い期限 10月25日」と同じ日。変えるときは両方を揃える。
const DEFAULT_DUE = "2026-10-25";
// 2次の支払期限の初期値。slash_website の申込ページ（SeatPurchase.js の PAYMENT_DUE_ROUND2）と揃える。
const DEFAULT_DUE_ROUND2 = "2026-12-27";
const defaultDue = (round) => (round === 2 ? DEFAULT_DUE_ROUND2 : DEFAULT_DUE);

function SeatFlowPanel({ data, post, refresh, setToast }) {
  const progress = data.progress;
  const stage = progress.stage;

  return (
    <div className="seat-flow">
      <ol className="seat-steps">
        {STEPS.map((s, i) => {
          const cur = STEPS.findIndex((x) => x.key === stage);
          const state = i < cur ? "done" : i === cur ? "current" : "todo";
          return (
            <li key={s.key} className={state}>
              <span className="n">{state === "done" ? <Icon name="check" /> : i + 1}</span>
              {s.label}
            </li>
          );
        })}
      </ol>

      {["deciding", "sending"].includes(stage) && (
        <InvoiceCard data={data} post={post} refresh={refresh} setToast={setToast} />
      )}

      {progress.overdue > 0 && <OverdueCard data={data} post={post} refresh={refresh} setToast={setToast} />}

      {stage === "sent" && (
        <div className="seat-flow__card">
          <p className="seat-flow__lead">
            当選・落選の全員に結果メールを送りました（{progress.mailed}家族）。入金は Stripe から自動で反映されます。
          </p>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- 請求と結果メール */
function InvoiceCard({ data, post, refresh, setToast }) {
  const progress = data.progress;
  const round = data.round || 1;
  const [due, setDue] = useState(defaultDue(round));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [run, setRun] = useState(null); // {done, total, errors: [], running}
  const stopRef = useRef(false);

  // 一覧が変わったら（当落の変更・送信の後）確認し直してもらう
  useEffect(() => setPreview(null), [data]);
  // 1次・2次を切り替えたら支払期限の初期値も入れ直す
  useEffect(() => setDue(defaultDue(round)), [round]);

  const attention = useMemo(() => data.applications.filter((a) => a.attention), [data]);

  const check = async () => {
    setBusy(true);
    try {
      setPreview(await post("preview_recital2027_seat_invoices", { due, round }));
    } catch (e) {
      setToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setConfirming(false);
    stopRef.current = false;
    const total = preview.invoices + preview.lost_mails;
    const state = { done: 0, total, errors: [], running: true };
    setRun({ ...state });
    try {
      for (;;) {
        if (stopRef.current) break;
        const res = await post("issue_recital2027_seat_invoices", { due, limit: BATCH, round });
        state.done += res.results.length;
        state.errors.push(...res.results.filter((r) => !r.ok));
        setRun({ ...state });
        const left = res.remaining.invoices + res.remaining.lost_mails;
        if (left === 0 || res.results.length === 0) break;
      }
    } catch (e) {
      state.errors.push({ id: "-", buyer_name: "", error: e.message });
    }
    state.running = false;
    setRun({ ...state });
    setToast(state.errors.length ? `送信を終えました（失敗 ${state.errors.length}件）` : "送信を終えました");
    await refresh();
  };

  const retry = async (a) => {
    if (!window.confirm(`${a.buyer_name} さんの送信をやり直しますか？（支払期限 ${due}）`)) return;
    try {
      const res = await post("issue_recital2027_seat_invoices", { due, only: a.id, round });
      const r = res.results[0];
      setToast(r.ok ? "送信しました" : `失敗しました: ${r.error}`);
      await refresh();
    } catch (e) {
      setToast(e.message);
    }
  };

  const unsent = progress.to_invoice + progress.to_lost_mail;
  const notable = preview
    ? preview.rows.filter((r) => r.compare !== "same" || r.pending || r.missing_family || r.round1_unpaid)
    : [];

  return (
    <div className="seat-flow__card">
      <h3>請求書と抽選結果メール{round === 2 ? "（2次）" : ""}</h3>
      <p className="seat-help">
        当選の家族には Stripe の請求書（1家族1通）と当選メール、落選の家族には落選メールを送ります。
        {round === 2
          ? "当落は下の申込一覧の当選席数で決めます（1席も無い家族は落選。その家族に結果メールを送るまで変えられます）。"
          : "当落は下の申込一覧のチェックで決めます（その家族に結果メールを送るまで変えられます）。"}
      </p>
      <p className="seat-flow__lead">
        未送信: 請求 <b>{progress.to_invoice}</b>件・落選メール <b>{progress.to_lost_mail}</b>件
        {progress.mailed ? `（送信済み ${progress.mailed}件）` : ""}
      </p>

      {attention.length > 0 && (
        <div className="seat-attention">
          <b>
            <Icon name="warning sign" /> 要確認 {attention.length}件
          </b>
          <span className="dim">送信の途中で止まった家族です。原因を確かめてから再送してください。</span>
          {attention.map((a) => (
            <div key={a.id} className="seat-attention__row">
              <span>
                {a.buyer_name}
                <span className="cell-sub">
                  {a.revoked_at
                    ? "当選の取り消しの途中で止まっています（再送で続きから行います）"
                    : a.status === "won" && a.has_invoice
                      ? "請求書を作った後に止まっています（Stripe で確認してください）"
                      : a.buyer_mail}
                </span>
              </span>
              <button type="button" className="ghost-btn" onClick={() => retry(a)}>
                再送
              </button>
            </div>
          ))}
        </div>
      )}

      {unsent > 0 && (
        <div className="seat-due">
          <label htmlFor="seat-due-date">支払期限（この日の 23:59 まで）</label>
          <div className="seat-due__row">
            <input
              id="seat-due-date"
              className="seat-input"
              type="date"
              value={due}
              onChange={(e) => {
                setDue(e.target.value);
                setPreview(null);
              }}
            />
            <button type="button" className="ghost-btn" disabled={busy || !due || run?.running} onClick={check}>
              請求内容を確認
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div className="seat-preview">
          <div className="kpis seat-preview__kpis">
            <div className="kpi">
              <span className="k">請求書</span>
              <span className="v">
                {preview.invoices}
                <small>件</small>
              </span>
              <span className="sub">
                合計 {yen(preview.total)}（税込）・うち自動引落 {preview.auto_charge}件
                {preview.promoted ? `・繰り上げ当選 ${preview.promoted}件` : ""}
              </span>
            </div>
            <div className="kpi">
              <span className="k">落選メール</span>
              <span className="v">
                {preview.lost_mails}
                <small>件</small>
              </span>
            </div>
            <div className={`kpi${preview.compare.mismatch ? " warn" : ""}`}>
              <span className="k">申込時の目安と比較</span>
              <span className="v small">一致 {preview.compare.same}</span>
              <span className="sub">
                出演部が変わった {preview.compare.parts_changed}・金額が合わない {preview.compare.mismatch}・比較しない{" "}
                {preview.compare.adjusted + preview.compare.unknown}
              </span>
            </div>
          </div>

          {preview.round1_unpaid > 0 && (
            <div className="alert">
              1次の請求が未払いのままの家族が {preview.round1_unpaid}件あります。両部割引は 1次の支払い済みの席だけで判定するので、
              1次を支払うか当選取消にしてから送るのが安全です。
            </div>
          )}

          {preview.compare.mismatch > 0 && (
            <div className="alert">
              申込時の目安と請求額が合わない家族がいます（両部の判定は同じなのに金額が違う＝計算がずれています）。直してから送ってください。
            </div>
          )}

          {notable.length > 0 && (
            <details className="seat-preview__list" open={preview.compare.mismatch > 0}>
              <summary>確認が必要な家族（{notable.length}件）</summary>
              <ul>
                {notable.map((r) => (
                  <li key={r.id} className={r.compare === "mismatch" || r.missing_family ? "bad" : ""}>
                    {r.buyer_name}
                    {r.external ? "（外部）" : ""} — 1部 {r.seats_1}席・2部 {r.seats_2}席
                    {r.total != null ? ` → ${yen(r.total)}` : ""}
                    {r.both_parts ? "（両部割引）" : ""}
                    <span className="tag">
                      {r.missing_family
                        ? "家族が見つかりません（送れません）"
                        : `${COMPARE_LABELS[r.compare]}${
                            r.applied_total != null && ["parts_changed", "mismatch"].includes(r.compare)
                              ? `・申込時 ${yen(r.applied_total)}`
                              : ""
                          }`}
                    </span>
                    {r.pending && <span className="tag warn">出演部が調整中の出演者あり</span>}
                    {r.round1_unpaid && <span className="tag warn">1次が未払い</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="seat-flow__actions">
            <button
              type="button"
              className="primary-btn"
              disabled={!preview.can_send || run?.running || preview.invoices + preview.lost_mails === 0}
              onClick={() => setConfirming(true)}
            >
              <Icon name="send" /> 送信する
            </button>
          </div>
        </div>
      )}

      {run && (
        <div className="seat-run">
          <div className="seat-run__bar">
            <span style={{ width: `${run.total ? Math.min(100, (run.done / run.total) * 100) : 100}%` }} />
          </div>
          <span>
            {run.done} / {run.total} 件{run.running ? " 送信中…" : " 完了"}
            {run.errors.length ? `・失敗 ${run.errors.length}件` : ""}
          </span>
          {run.running && (
            <button type="button" className="ghost-btn" onClick={() => { stopRef.current = true; }}>
              止める
            </button>
          )}
          {run.errors.length > 0 && (
            <ul className="seat-run__errors">
              {run.errors.map((r, i) => (
                <li key={`${r.id}-${i}`}>
                  {r.buyer_name || `#${r.id}`}: {r.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {confirming && (
        <ConfirmModal title="請求書と結果メールを送りますか？" busy={false} onCancel={() => setConfirming(false)} onOk={send} okLabel="送信する">
          <p>
            <b>本番の Stripe</b> で請求書 {preview.invoices}件（合計 {yen(preview.total)}）を発行し、申込者のメールに届きます。
            あわせて当選メール {preview.invoices}件・落選メール {preview.lost_mails}件を送ります。支払期限は {preview.due} です。
          </p>
          {preview.auto_charge > 0 && (
            <p>
              自動引落の {preview.auto_charge}件は、マイページアプリに登録のカードでその場で引き落とします
              （引き落とせなかった家族には、請求書のリンクから支払ってもらうメールを送ります）。
            </p>
          )}
          <p>
            送信を始めた時点で当落が確定し、送った家族の当落は変えられなくなります。{BATCH}件ずつ送り、途中で止めても続きから再開できます。
          </p>
        </ConfirmModal>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- 期限切れの未払い */
function OverdueCard({ data, post, refresh, setToast }) {
  const [busy, setBusy] = useState(false);
  const targets = useMemo(
    () => data.applications.filter((a) => a.status === "invoiced" && a.overdue && a.can_revoke),
    [data],
  );

  const revokeAll = async () => {
    if (
      !window.confirm(
        `支払期限を過ぎた未払いの ${targets.length}家族の当選を取り消しますか？\n請求書を無効にし、取り消しのメールを送ります。`,
      )
    )
      return;
    setBusy(true);
    try {
      const res = await post("revoke_recital2027_seat_applications", { ids: targets.map((a) => a.id) });
      const failed = res.results.filter((r) => !r.ok);
      setToast(
        failed.length
          ? `取り消しました（取り消せなかった家族 ${failed.length}件: ${failed.map((r) => `${r.buyer_name} ${r.error}`).join(" / ")}）`
          : `${res.results.length}家族の当選を取り消しました`,
      );
      await refresh();
    } catch (e) {
      setToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="seat-flow__card">
      <h3>期限切れの未払い</h3>
      <p className="seat-flow__lead">
        支払期限を過ぎた未払いが <b>{targets.length}</b>件あります。
      </p>
      <p className="seat-help">
        当選を取り消すと、請求書を無効にして取り消しのメールを送ります。空いた席は、申込一覧の落選の家族を「繰り上げ当選」にして、
        「請求書と抽選結果メール」から送ってください。1家族ずつ取り消すときは、申込一覧の「当選取消」を使います。
      </p>
      <div className="seat-flow__actions">
        <button type="button" className="danger-btn" disabled={busy || targets.length === 0} onClick={revokeAll}>
          まとめて当選取消
        </button>
      </div>
    </div>
  );
}

function ConfirmModal({ title, children, onCancel, onOk, okLabel, busy }) {
  return (
    <div className="seat-modal-overlay" onClick={() => !busy && onCancel()}>
      <div className="seat-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <div className="seat-confirm-body">{children}</div>
        <div className="seat-modal-actions">
          <button type="button" className="ghost-btn" disabled={busy} onClick={onCancel}>
            キャンセル
          </button>
          <button type="button" className="primary-btn" disabled={busy} onClick={onOk}>
            {okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SeatFlowPanel;
